import { FORTA_BLUE, FORTA_GRAY2, FORTA_WHITE } from '../../../utils/colors';
import {
  SCHEDULE_BLOCK_MIN_HEIGHT,
  SCHEDULE_HEADER_HEIGHT,
  SCHEDULE_HOUR_HEIGHT,
  SCHEDULE_HOUR_HEIGHT_MOBILE,
  SCHEDULE_HOUR_LINE_THICKNESS,
  SCHEDULE_TIME_COLUMN_MOBILE_WIDTH,
  SCHEDULE_TIME_COLUMN_WIDTH,
  SCHEDULE_TOTAL_HOURS,
} from './constants';

import type { SxProps, Theme } from '@mui/material';
import type { PositionedScheduleItem } from './types';

export const WEEKLY_SCHEDULE_ROOT_SX: SxProps<Theme> = {
  width: '100%',
  border: `1px solid ${FORTA_GRAY2}`,
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: FORTA_WHITE,
};

export const WEEKLY_SCHEDULE_GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: `${SCHEDULE_TIME_COLUMN_MOBILE_WIDTH}px repeat(7, minmax(0, 1fr))`,
    sm: `${SCHEDULE_TIME_COLUMN_WIDTH}px repeat(7, minmax(0, 1fr))`,
  },
};

export const WEEKLY_SCHEDULE_CORNER_CELL_SX: SxProps<Theme> = {
  height: SCHEDULE_HEADER_HEIGHT,
  backgroundColor: FORTA_BLUE.light1,
};

export const WEEKLY_SCHEDULE_HEADER_CELL_SX: SxProps<Theme> = {
  height: SCHEDULE_HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: FORTA_WHITE,
  backgroundColor: FORTA_BLUE.light1,
  borderLeft: `1px solid ${FORTA_BLUE.light2}`,
  fontSize: 13,
  fontWeight: 600,
};

export const WEEKLY_SCHEDULE_TIME_CELL_SX: SxProps<Theme> = {
  position: 'relative',
  height: { xs: SCHEDULE_HOUR_HEIGHT_MOBILE, sm: SCHEDULE_HOUR_HEIGHT },
  display: 'flex',
  alignItems: 'flex-start',
  pl: 1.5,
  pt: 0.5,
  color: FORTA_WHITE,
  backgroundColor: FORTA_BLUE.light1,
  boxSizing: 'border-box',
};

export const WEEKLY_SCHEDULE_TIME_LABEL_SX: SxProps<Theme> = {
  mt: '-6px',
  fontSize: { xs: 10, sm: 12 },
  lineHeight: 1,
};

export const WEEKLY_SCHEDULE_TIME_TICK_SX: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: 8,
  borderTop: `1px solid ${FORTA_WHITE}`,
  opacity: 0.6,
};

export const WEEKLY_SCHEDULE_DAY_COLUMN_SX: SxProps<Theme> = {
  position: 'relative',
  height: {
    xs: SCHEDULE_HOUR_HEIGHT_MOBILE * SCHEDULE_TOTAL_HOURS,
    sm: SCHEDULE_HOUR_HEIGHT * SCHEDULE_TOTAL_HOURS,
  },
  borderLeft: `1px solid ${FORTA_GRAY2}`,
  backgroundImage: `linear-gradient(to bottom, ${FORTA_GRAY2} 1px, transparent 1px)`,
  backgroundSize: {
    xs: `100% ${SCHEDULE_HOUR_HEIGHT_MOBILE}px`,
    sm: `100% ${SCHEDULE_HOUR_HEIGHT}px`,
  },
};

export const WEEKLY_SCHEDULE_BLOCK_TOP_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const WEEKLY_SCHEDULE_BLOCK_LABEL_SX: SxProps<Theme> = {
  fontSize: { xs: 10, sm: 12 },
  fontWeight: 500,
  lineHeight: 1.4,
};

export const WEEKLY_SCHEDULE_TOOLTIP_TITLE_SX: SxProps<Theme> = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
};

export const WEEKLY_SCHEDULE_TOOLTIP_TIME_SX: SxProps<Theme> = {
  display: 'block',
  fontSize: 12,
};

export const WEEKLY_SCHEDULE_DELETE_BUTTON_SX: SxProps<Theme> = {
  p: 0,
  ml: { xs: 0, sm: 0.5 },
  color: FORTA_WHITE,
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.85,
  },
};

const WEEKLY_SCHEDULE_BLOCK_BOTTOM_DIVIDER = {
  borderBottom: `1px solid ${FORTA_WHITE}`,
};

export function buildScheduleBlockHourLineSx(hourOffset: number): SxProps<Theme> {
  // 1px boxes (not a 0.5px gradient) so hour lines stay visible when the browser is zoomed out.
  return {
    position: 'absolute',
    left: 0,
    right: 0,
    top: {
      xs: hourOffset * SCHEDULE_HOUR_HEIGHT_MOBILE - SCHEDULE_HOUR_LINE_THICKNESS,
      sm: hourOffset * SCHEDULE_HOUR_HEIGHT - SCHEDULE_HOUR_LINE_THICKNESS,
    },
    height: `${SCHEDULE_HOUR_LINE_THICKNESS}px`,
    backgroundColor: FORTA_WHITE,
    pointerEvents: 'none',
  };
}

export function buildScheduleBlockSx(
  item: PositionedScheduleItem,
  showBottomDivider: boolean,
): SxProps<Theme> {
  return {
    position: 'absolute',
    top: {
      xs: item.startHourOffset * SCHEDULE_HOUR_HEIGHT_MOBILE,
      sm: item.startHourOffset * SCHEDULE_HOUR_HEIGHT,
    },
    left: 0,
    right: 0,
    height: {
      xs: Math.max(item.durationHours * SCHEDULE_HOUR_HEIGHT_MOBILE, SCHEDULE_BLOCK_MIN_HEIGHT),
      sm: Math.max(item.durationHours * SCHEDULE_HOUR_HEIGHT, SCHEDULE_BLOCK_MIN_HEIGHT),
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: { xs: 'center', sm: 'stretch' },
    textAlign: { xs: 'center', sm: 'left' },
    px: 0.75,
    py: 0.25,
    boxSizing: 'border-box',
    overflow: 'hidden',
    color: FORTA_WHITE,
    backgroundColor: item.activity_color,
    ...(showBottomDivider && WEEKLY_SCHEDULE_BLOCK_BOTTOM_DIVIDER),
  };
}
